import { h, tidy } from "../shared/safe";
import { errorBanner, loadingBlock, tableRegion } from "../shared/blocks";
import { platformLabel } from "../../platforms";
import { actionButton, noticeBlock, statusPill, webLink } from "./bits";
import type { Ctx, DeliveryEntry } from "./context";
import { editorialStatus, type DeliveryDestinationView, type HistoryEntryView, type ReceiptView } from "./model";
import { describeDelivery, describeDestination, type ApproveOutcome, type PublishOutcome } from "./outcomes";

// Renders the honest results of approving and publishing, and the delivery record. Four things stay visibly separate
// everywhere they appear: the article's own (editorial) status, ACCEPTANCE of a publish request ("queued", possibly still
// waiting for the dispatcher), what each destination currently holds (its recorded state and immutable receipt), and what
// still needs reconciliation.

const JOB_LABELS: Record<string, string> = { queued: "queued", running: "running", done: "finished", failed: "failed, will retry", dead: "failed" };
const jobLabel = (status: string) => (Object.prototype.hasOwnProperty.call(JOB_LABELS, status) ? JOB_LABELS[status] : tidy(status, 24));

function effectLabel(d: { sideEffect: string | null }): string {
  if (d.sideEffect === "confirmed") return "Confirmed at the provider";
  if (d.sideEffect === "possible") return "Possible effect, not verified";
  return "No effect recorded";
}

export function approveOutcomeBlock(o: ApproveOutcome | null | undefined): HTMLElement | null {
  if (!o) return null;
  return noticeBlock({ tone: o.kind === "ok" ? "ok" : o.kind === "uncertain" ? "warn" : "bad", text: o.message, detail: [...o.errors, ...(o.advice ? [o.advice] : [])] });
}

export function publishOutcomeBlock(o: PublishOutcome | null | undefined): HTMLElement | null {
  if (!o) return null;
  const partial = o.kind === "accepted" && (o.failed.length > 0 || o.skipped.length > 0 || (o.dispatch?.pending ?? 0) > 0);
  const detail: string[] = [];
  if (o.detail) detail.push(o.detail);
  for (const a of o.accepted) detail.push(`Queued: ${platformLabel(a.platform)}${a.kind ? ` (${tidy(a.kind, 30)})` : ""}`);
  for (const f of o.failed) detail.push(`Could not be queued: ${platformLabel(f.platform)}: ${f.error}`);
  for (const s of o.skipped) detail.push(`Skipped: ${platformLabel(s.platform)}: ${s.reason}`);
  for (const e of o.errors) detail.push(e);
  if (o.note) detail.push(`Note: ${o.note}`);
  if (o.advice) detail.push(o.advice);
  return h(
    "div",
    { class: "qf-cd-outcome", dataset: { outcome: o.kind, code: o.code ?? undefined, replayed: o.replayed ? "true" : undefined, dispatchPending: o.dispatch && o.dispatch.pending > 0 ? String(o.dispatch.pending) : undefined } },
    noticeBlock({ tone: o.kind === "refused" ? "bad" : o.kind === "uncertain" ? "warn" : partial ? "warn" : "info", text: o.headline, detail, link: o.needsConnection ? { text: "Go to Connections", href: "#/connections" } : undefined }),
  );
}

/** "account conn_x, credentials revision 2": which account (and credential generation) an attempt or receipt belongs to. */
const accountText = (connectionId: string | null, revision: number | null): string | null =>
  connectionId === null ? null : `account ${tidy(connectionId, 40)}${revision === null ? " (revision not recorded)" : `, credentials revision ${revision}`}`;

/**
 * `current`: this attempt's own account's receipt. `earlier`: an earlier attempt's receipt through the same account (history).
 * `other-account`: a receipt from a different account or credential revision: history, never this account's delivery.
 */
type ReceiptKind = "current" | "earlier" | "other-account";
const RECEIPT_LABEL: Record<ReceiptKind, string> = { current: "Receipt: ", earlier: "Earlier receipt: ", "other-account": "Receipt from another account (history only): " };

/** The local synthetic provider (LOCAL-INTERFACES s5) answers with https://<slug>.synthetic.invalid/. The name can never resolve, so such a receipt is not a real publication. */
const isSyntheticReceipt = (r: ReceiptView): boolean => {
  if (r.providerUrl?.href == null) return false;
  try {
    return new URL(r.providerUrl.href).hostname.endsWith(".synthetic.invalid");
  } catch {
    return false;
  }
};

function receiptBlock(r: ReceiptView, kind: ReceiptKind): HTMLElement {
  const account = accountText(r.connectionId, r.accountRevision);
  return h(
    "div",
    { class: "qf-cd-receipt", dataset: { receipt: kind, synthetic: isSyntheticReceipt(r) ? "true" : undefined } },
    h("span", { class: "muted" }, RECEIPT_LABEL[kind]),
    webLink(r.providerUrl),
    isSyntheticReceipt(r) ? h("strong", { class: "muted" }, " · SYNTHETIC receipt: a counted fake provider, not a real publication") : null,
    r.recordedAt ? h("span", { class: "muted" }, ` · recorded ${r.recordedAt}`) : null,
    r.accountProvenance ? h("span", { class: "muted" }, ` · account: ${r.accountProvenance}`) : null,
    account ? h("span", { class: "muted" }, ` · ${account}`) : null,
  );
}

function destinationRow(d: DeliveryDestinationView): HTMLElement {
  const desc = describeDestination(d);
  return h(
    "tr",
    { dataset: { jobStatus: d.jobStatus, sideEffect: d.sideEffect ?? "none", destinationState: d.state ?? "legacy", inFlight: d.inFlight ? "true" : "false", dispatch: d.dispatch ?? "unknown" } },
    h("td", null, platformLabel(d.platform), accountText(d.connectionId, d.accountRevision) ? h("div", { class: "muted qf-small" }, accountText(d.connectionId, d.accountRevision)) : null),
    h("td", null, statusPill(desc.label, desc.tone), desc.note ? h("div", { class: "muted qf-small" }, desc.note) : null),
    h("td", null, `Latest attempt: ${jobLabel(d.jobStatus)}`, h("div", { class: "muted qf-small" }, effectLabel(d))),
    h(
      "td",
      null,
      d.receipt ? receiptBlock(d.receipt, d.inFlight || d.state === "queued" || (d.receipt.jobId !== null && d.receipt.jobId !== d.jobId) ? "earlier" : "current") : h("span", { class: "muted" }, "No receipt recorded for this account"),
      ...d.priorReceipts.map((r) => receiptBlock(r, "other-account")),
    ),
    h("td", null, d.attempts === null ? "—" : String(d.attempts)),
    h("td", null, d.lastError ?? "—"),
  );
}

function historyRow(x: HistoryEntryView): HTMLElement {
  return h(
    "tr",
    { dataset: { historyJob: x.jobId } },
    h("td", null, platformLabel(x.platform), accountText(x.connectionId, x.accountRevision) ? h("div", { class: "muted qf-small" }, accountText(x.connectionId, x.accountRevision)) : null),
    h("td", null, tidy(x.submissionId ?? "older submission", 40)),
    h("td", null, jobLabel(x.jobStatus), h("div", { class: "muted qf-small" }, effectLabel(x))),
    h("td", null, x.receipt ? receiptBlock(x.receipt, "earlier") : "—"),
    h("td", null, x.lastError ?? "—"),
  );
}

export function deliveryPanel(ctx: Ctx, masterId: string, entry: DeliveryEntry | undefined): HTMLElement {
  if (!entry || entry.state === "loading") return loadingBlock("Loading delivery", 2);
  if (entry.state === "error") return errorBanner(entry.message ?? "Delivery could not be loaded.", { retry: () => void ctx.actions.refreshDelivery(masterId), label: "Try again" });
  const d = entry.delivery;
  if (!d) return h("p", { class: "muted" }, "The server did not return delivery information for this article.");
  const desc = describeDelivery(d);
  const editorial = entry.editorial ? editorialStatus(entry.editorial) : null;
  const table = d.destinations.length
    ? tableRegion(
        "Delivery per destination",
        h(
          "table",
          null,
          h("thead", null, h("tr", null, h("th", null, "Destination"), h("th", null, "Current state"), h("th", null, "Latest attempt"), h("th", null, "Provider receipt"), h("th", null, "Attempts"), h("th", null, "Last error"))),
          h("tbody", null, ...d.destinations.map(destinationRow)),
        ),
      )
    : null;
  // Earlier attempts are kept apart from the current state: listed, never merged, never summed into "delivered".
  const current = new Set(d.destinations.map((x) => x.jobId));
  const earlier = d.history.filter((x) => !current.has(x.jobId));
  const history = earlier.length
    ? h(
        "details",
        { class: "qf-cd-history" },
        h("summary", null, `Earlier attempts (${earlier.length})`),
        tableRegion(
          "Earlier delivery attempts",
          h("table", null, h("thead", null, h("tr", null, h("th", null, "Destination"), h("th", null, "Submission"), h("th", null, "Attempt"), h("th", null, "Receipt"), h("th", null, "Last error"))), h("tbody", null, ...earlier.map(historyRow))),
        ),
      )
    : null;
  return h(
    "div",
    { class: "qf-cd-delivery", dataset: { deliveryState: d.state } },
    h(
      "p",
      { class: "qf-cd-delivery__heads" },
      h("span", null, "Article status: ", editorial ? statusPill(editorial.label, editorial.tone) : "unknown"),
      h("span", null, "Delivery: ", statusPill(desc.label, desc.tone)),
    ),
    h("p", null, desc.summary),
    desc.reconciliationNote ? noticeBlock({ tone: "warn", text: desc.reconciliationNote, link: { text: "Open the Queue", href: "#/queue" } }) : null,
    table,
    history,
    d.submissions.length > 1 ? h("p", { class: "muted" }, `${d.submissions.length} submissions have been made for this article.`) : null,
    d.state === "not_submitted" ? null : h("p", { class: "muted" }, "Final outcomes also appear on the ", h("a", { href: "#/queue" }, "Queue"), " page as jobs finish."),
    actionButton({ ctx, key: `delivery-load:${masterId}`, focusKey: `delivery-refresh:${masterId}`, label: "Refresh delivery", busyLabel: "Refreshing…", variant: "ghost", onClick: () => ctx.actions.refreshDelivery(masterId) }),
  );
}
